import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { fetchConsentedVehicles, revokeConsent } from '../../service/consents-api'
import type { GetConsentsResponse } from '../../types/consent.types'
import type { AppError } from '../../types/error.types'
import { toAppError } from '../../utils/ErrorHandler'

export type ConsentsState = {
  items: GetConsentsResponse
  loading: boolean
  revokingConsentId?: string
  error?: AppError
}

const initialState: ConsentsState = {
  items: [],
  loading: false,
}

export const fetchConsentsThunk = createAsyncThunk<
  GetConsentsResponse,
  undefined,
  { rejectValue: AppError }
>('consents/fetchConsents', async (_, { rejectWithValue }) => {
  try {
    return await fetchConsentedVehicles()
  } catch (error) {
    return rejectWithValue(toAppError(error, { fallbackMessage: 'Failed to fetch consents.' }))
  }
})

export const deleteConsentThunk = createAsyncThunk<string, string, { rejectValue: AppError }>(
  'consents/deleteConsent',
  async (consentId, { rejectWithValue }) => {
    try {
      await revokeConsent(consentId)
      return consentId
    } catch (error) {
      return rejectWithValue(toAppError(error, { fallbackMessage: 'Failed to revoke consent.' }))
    }
  },
)

const consentsSlice = createSlice({
  name: 'consents',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchConsentsThunk.pending, (state) => {
        state.loading = true
        state.error = undefined
      })
      .addCase(fetchConsentsThunk.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload
      })
      .addCase(fetchConsentsThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(deleteConsentThunk.pending, (state, action) => {
        state.revokingConsentId = action.meta.arg
        state.error = undefined
      })
      .addCase(deleteConsentThunk.fulfilled, (state, action) => {
        state.revokingConsentId = undefined
        const consentId = action.payload

        state.items = state.items
          .map((record) => ({
            ...record,
            consents: record.consents.filter((consent) => consent.id !== consentId),
          }))
          .filter((record) => record.consents.length > 0)
      })
      .addCase(deleteConsentThunk.rejected, (state, action) => {
        state.revokingConsentId = undefined
        state.error = action.payload
      })
  },
})

export const consentsReducer = consentsSlice.reducer
